import { useCallback, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import * as yup from 'yup';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import i18n from '@/i18n/i18n';
import { cn } from '@/lib/utils';
import { SlackIntegration } from '@/shared/types/integration';
import { createYupResolver } from '@/util/forms/createYupResolver';

interface SlackConfigurationProps {
  integration: SlackIntegration | undefined;
  canWrite?: boolean;
}

type SlackConfigFormData = { signing_secret: string; access_token: string };

const slackConfigSchema = yup
  .object({
    signing_secret: yup
      .string()
      .trim()
      .required(() => i18n.t('integrations.slack.missing_signing_secret'))
      .defined(),
    access_token: yup
      .string()
      .trim()
      .required(() => i18n.t('integrations.slack.missing_access_token'))
      .defined(),
  })
  .required();

export const SlackConfiguration = ({ integration, canWrite = true }: SlackConfigurationProps) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { integrationId } = useParams();
  const { updateIntegration, updateStatus } = useIntegrations<SlackIntegration>(
    deskId,
    integrationId
  );

  const [accessTokenError, setAccessTokenError] = useState<string | null>(null);

  const isNewIntegration =
    !integration?.config?.signing_secret && !integration?.config?.access_token;

  const form = useForm<SlackConfigFormData>({
    resolver: createYupResolver<SlackConfigFormData>(slackConfigSchema),
    defaultValues: {
      signing_secret: integration?.config?.signing_secret || '',
      access_token: integration?.config?.access_token || '',
    },
    mode: 'onSubmit',
  });

  const {
    formState: { isSubmitting, isDirty },
  } = form;

  const validateAccessToken = useCallback(
    (token: string): boolean => {
      // Slack bot tokens always start with 'xoxb-' prefix
      // See: https://api.slack.com/authentication/token-types
      if (!token.startsWith('xoxb-')) {
        setAccessTokenError(t('integrations.slack.invalid_access_token'));
        return false;
      }
      setAccessTokenError(null);
      return true;
    },
    [t]
  );

  const onValidSubmit = useCallback(
    (data: SlackConfigFormData) => {
      // Validate access token format
      if (!validateAccessToken(data.access_token)) {
        return;
      }

      updateIntegration({
        ...integration,
        config: {
          ...integration?.config,
          signing_secret: data.signing_secret,
          access_token: data.access_token,
          session_timeout: 86400,
        },
        mutationOptions: { successDescription: t('integrations.slack.callback_url_reminder') },
      });
    },
    [integration, t, updateIntegration, validateAccessToken]
  );

  return (
    <div className="flex flex-col gap-2">
      <p className="px-1.5 text-sm leading-none font-medium text-muted-foreground">
        {t('integrations.slack.configuration')}
      </p>
      <Card className="py-0 shadow-none">
        <CardContent className="px-4 pt-4 pb-5">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onValidSubmit)} className="flex flex-col gap-4">
              {/* Signing Secret Field */}
              <FormField
                control={form.control}
                name="signing_secret"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>{t('integrations.slack.signing_secret')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        placeholder={t('integrations.slack.signing_secret_placeholder')}
                        disabled={!canWrite}
                        autoFocus={isNewIntegration && canWrite}
                      />
                    </FormControl>
                    <FormDescription>{t('integrations.slack.signing_secret_help')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Access Token Field */}
              <FormField
                control={form.control}
                name="access_token"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className={accessTokenError ? 'text-warning' : undefined}>
                      {t('integrations.slack.access_token')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('integrations.slack.access_token_placeholder')}
                        disabled={!canWrite}
                        onChange={(e) => {
                          field.onChange(e);
                          // Clear error when user types
                          if (accessTokenError) {
                            setAccessTokenError(null);
                          }
                        }}
                      />
                    </FormControl>
                    <FormDescription>{t('integrations.slack.access_token_help')}</FormDescription>
                    {accessTokenError && (
                      <p className="text-sm font-medium text-warning">{accessTokenError}</p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Animated Save Button */}
              <div
                className={cn(
                  'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
                  isDirty ? 'grid-rows-expanded opacity-100' : 'grid-rows-collapsed opacity-0'
                )}
              >
                <div className="overflow-hidden px-0.25 pb-0.75">
                  <div className="flex justify-end pt-2">
                    <Button
                      type="submit"
                      disabled={!canWrite}
                      isLoading={updateStatus === 'pending' || isSubmitting}
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
